/**
 * Constants for Caster Mold 3D Map View
 *
 * GLB Mesh Mapping: Mold GLB meshes are named "Solid1_N" (unique per mesh).
 * Component identity lives in the GROUP parent node names (underscored,
 * no colons — e.g. "7x7_Water_Jacket_Flume1"). The traversal logic in
 * MoldModel.tsx walks up to the nearest GROUP parent to resolve a position.
 *
 * Mapping source: Automated dump of MoldMESModel.glb group node names,
 * cross-referenced with catPartTypesTree positions from seed-caster.sql.
 * Hardware parts (screws, nuts, bushing spacers) are intentionally unmapped.
 */
import { Viewer3DSettings } from "../../../../shared/theme";

// ============================================
// Node Mapping Interface
// ============================================

/**
 * Maps a GLB group node name to its position in the EQP hierarchy.
 *
 * - positionId: Mold direct child position (1-13)
 * - subPositionId: Sub-component position within a group (optional)
 *
 * For standalone components (positions 1-6), only positionId is set.
 * For group children (Flanges/Collars/Seals/FootRoll), both are set.
 */
export interface NodeMapping {
  positionId: number;
  subPositionId?: number;
}

/**
 * Maps Foot Roll Assembly parent nodes to their EQP position.
 *
 * The GLB has 4 assemblies but ALL their children share the same names
 * (e.g., "7x7 Foot Roll:1" appears in every assembly). The traversal
 * must walk up to the Assembly parent to disambiguate which position
 * a child belongs to.
 */
export const ASSEMBLY_TO_POSITION: Readonly<Record<string, number>> = {
  "7x7_Foot_Roll_Assembly1": 10, // Left
  "7x7_Foot_Roll_Assembly2": 11, // Right
  "7x7_Foot_Roll_Assembly3": 12, // Internal
  "7x7_Foot_Roll_Assembly4": 13, // External
};

/**
 * Maps Foot Roll child node names to their sub-position within ANY assembly.
 * Combined with ASSEMBLY_TO_POSITION to produce the full NodeMapping.
 *
 * All 4 assemblies have children with identical names (all ":1"),
 * so we strip the suffix and map the base name.
 */
export const FOOT_ROLL_CHILD_TO_SUB: Readonly<Record<string, number>> = {
  // Assembly 1 (Left) — base names, no suffix
  "7x7_Foot_Roll1": 1,
  "7x7_Foot_Roll_Pin1": 2,
  "7x7_Roller_Support1": 3,
  Roll_Keeper_Plates1: 4,
  // Assembly 2 (Right) — _1 suffix
  "7x7_Foot_Roll1_1": 1,
  "7x7_Foot_Roll_Pin1_1": 2,
  "7x7_Roller_Support1_1": 3,
  Roll_Keeper_Plates1_1: 4,
  // Assembly 3 (Internal) — _2 suffix
  "7x7_Foot_Roll1_2": 1,
  "7x7_Foot_Roll_Pin1_2": 2,
  "7x7_Roller_Support1_2": 3,
  Roll_Keeper_Plates1_2: 4,
  // Assembly 4 (External) — _3 suffix
  "7x7_Foot_Roll1_3": 1,
  "7x7_Foot_Roll_Pin1_3": 2,
  "7x7_Roller_Support1_3": 3,
  Roll_Keeper_Plates1_3: 4,
};

// ============================================
// GLB Group Node → Position Mapping
// ============================================

/**
 * Maps GLB GROUP node names to EQP hierarchy positions.
 *
 * Position IDs match catPartTypesTree positions from seed-caster.sql:
 *   1: Copper Mold Tube       7: Flanges (group)      10: Foot Roll Left
 *   2: Mold Body              8: Collars (group)      11: Foot Roll Right
 *   3: Oil Distribution Gasket 9: Seals (group)        12: Foot Roll Internal
 *   4: Level Control                                  13: Foot Roll External
 *   5: Oil Connector
 *   6: Water Jacket Flume
 */
export const GLB_NODE_TO_POSITION: Readonly<Record<string, NodeMapping>> = {
  // ==========================================================================
  // Standalone Components (direct children of Mold, positions 1-6)
  // ==========================================================================

  // Position 1: Copper Mold Tube
  "7x7_mold1": { positionId: 1 },

  // Position 2: Mold Body
  New_Style_STS_Mold_Body1: { positionId: 2 },

  // Position 3: Oil Distribution Gasket
  "7x7_MoldOilDistPlate1": { positionId: 3 },

  // Position 4: Level Control
  LevelControlPlaceholder1: { positionId: 4 },

  // Position 5: Oil Connector
  Mold_Oil_Connector1: { positionId: 5 },

  // Position 6: Water Jacket Flume
  "7x7_Water_Jacket_Flume1": { positionId: 6 },

  // ==========================================================================
  // Flanges Group (position 7, sub-positions 1-5)
  // ==========================================================================

  // Sub 1: Upper Flange
  "7x7_Upper_Flange1": { positionId: 7, subPositionId: 1 },

  // Sub 2: Low Upper Flange
  "7x7_Low_Upper_Flange1": { positionId: 7, subPositionId: 2 },

  // Sub 3: Lower Flange
  "7x7_Lower_Flange1": { positionId: 7, subPositionId: 3 },

  // Sub 4: Closing Flange
  "7x7_Closing_Flange1": { positionId: 7, subPositionId: 4 },

  // Sub 5: Water Jacket Fixing Flange
  "7x7_Water_Jacket_Fixing_Flange1": { positionId: 7, subPositionId: 5 },

  // ==========================================================================
  // Collars Group (position 8, sub-positions 1-2)
  // ==========================================================================

  // Sub 1: Upper Collar
  "7x7_Upper_Collar1": { positionId: 8, subPositionId: 1 },

  // Sub 2: Lower Collar
  "7x7_Lower_Collar1": { positionId: 8, subPositionId: 2 },

  // ==========================================================================
  // Seals Group (position 9, sub-positions 1-8)
  // ==========================================================================

  // Sub 1: Upper Seal
  Upper_Seal1: { positionId: 9, subPositionId: 1 },

  // Sub 2: Copper Tube Seal Upper
  Copper_Tube_Seal1: { positionId: 9, subPositionId: 2 },

  // Sub 3: Copper Tube Seal Lower
  Copper_Tube_Seal2: { positionId: 9, subPositionId: 3 },

  // Sub 4: Water Jacket Seal
  Water_Jacket_Seal1: { positionId: 9, subPositionId: 4 },

  // Sub 5: Lower Internal Seal
  Lower_Internal_Seal1: { positionId: 9, subPositionId: 5 },

  // Sub 6: Lower External Seal
  Lower_External_Seal1: { positionId: 9, subPositionId: 6 },

  // Sub 7: Oil Connector O-Ring Upper
  Oil_Connector_ORing1: { positionId: 9, subPositionId: 7 },

  // Sub 8: Oil Connector O-Ring Lower
  Oil_Connector_ORing2: { positionId: 9, subPositionId: 8 },

  // ==========================================================================
  // Foot Roll Assemblies — Z1 Sprays & Spray Nozzles (root-level nodes)
  // ==========================================================================
  // NOTE: Foot Roll/Pin/Roller Support/Roll Keeper Plates are resolved via
  // ASSEMBLY_TO_POSITION + FOOT_ROLL_CHILD_TO_SUB in the traversal logic.

  // Left Foot Roll Assembly (position 10)
  "7x7_Left_Z1_Spray1": { positionId: 10, subPositionId: 5 },
  Spray_Nozzles1: { positionId: 10, subPositionId: 7 },
  Spray_Nozzles2: { positionId: 10, subPositionId: 6 },

  // Right Foot Roll Assembly (position 11)
  "7x7_Right_Z1_Spray1": { positionId: 11, subPositionId: 5 },
  Spray_Nozzles3: { positionId: 11, subPositionId: 7 },
  Spray_Nozzles4: { positionId: 11, subPositionId: 6 },

  // Internal Foot Roll Assembly (position 12)
  "7x7_Interior_Z1_Spray1": { positionId: 12, subPositionId: 5 },
  Spray_Nozzles_MIR1: { positionId: 12, subPositionId: 7 },
  Spray_Nozzles_MIR2: { positionId: 12, subPositionId: 6 },

  // External Foot Roll Assembly (position 13)
  "7x7_Exterior_Z1_Spray1": { positionId: 13, subPositionId: 5 },
  Spray_Nozzles6: { positionId: 13, subPositionId: 7 },
  Spray_Nozzles7: { positionId: 13, subPositionId: 6 },
};

/**
 * Total number of mapped group nodes.
 * GLB_NODE_TO_POSITION: 6 standalone + 5 flanges + 2 collars + 8 seals + 12 FR sprays/nozzles = 33
 * FOOT_ROLL_CHILD_TO_SUB: 4 child types × 4 assemblies = 16 (resolved via Assembly parent)
 * ASSEMBLY_TO_POSITION: 4 assembly parents
 * Effective total: 33 + 16 = 49 unique positions
 */
export const MAPPED_NODE_COUNT = 49;

// ============================================
// Empty Position Color
// ============================================

/** Gray color for empty positions (no part installed) */
export const EMPTY_POSITION_COLOR = "#9e9e9e";

// ============================================
// Hover / Selection Highlight Intensity
// ============================================

export const HOVER_EMISSIVE_INTENSITY =
  Viewer3DSettings.HOVER_EMISSIVE_INTENSITY;
export const SELECTION_EMISSIVE_INTENSITY =
  Viewer3DSettings.SELECTION_EMISSIVE_INTENSITY;

// ============================================
// Camera Settings
// ============================================

/**
 * Camera positioned to show the Mold from a slightly elevated front-right angle.
 * The mold is a tall vertical assembly (~1.2m high), so camera is set further
 * back and higher than the Sidewall view.
 */
export const CAMERA_SETTINGS = {
  position: [0, 0.8, 2.0] as [number, number, number],
  fov: 45,
  near: 0.1,
  far: 100,
};

// ============================================
// OrbitControls Settings
// ============================================

export const ORBIT_CONTROLS_SETTINGS = {
  enablePan: true,
  enableZoom: true,
  enableRotate: true,
  minDistance: 0.5,
  maxDistance: 8,
  minPolarAngle: 0,
  maxPolarAngle: Math.PI / 1.8, // ~100 degrees - allow slightly more tilt for vertical model
  enableDamping: true,
  dampingFactor: 0.05,
  target: [0, 0.3, 0] as [number, number, number], // Center on mold mid-height
};

// ============================================
// Model Position & Scale
// ============================================

/** Model position offset (to center the GLB model in the scene) */
export const MODEL_POSITION = [0, 0, 0] as [number, number, number];

/**
 * Model scale factor. The Mold GLB is exported in meters (same as Sidewall/Roof).
 * At scale 1.0, a ~1.2m tall mold renders as ~1.2 scene units,
 * which fits the camera setup (position [0, 0.8, 2.0], target [0, 0.3, 0]).
 */
export const MODEL_SCALE = 1.0;

// ============================================
// Lighting Settings
// ============================================

export const LIGHTING_SETTINGS = {
  ambientIntensity: 0.6,
  directionalIntensity: 0.8,
  directionalPosition: [5, 10, 5] as [number, number, number],
};
